import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { enrichWithAnchoredVwap, latestAnchoredVwap } from "../utils/vwap";

/**
 * Fetches the price-trend chart data for `(ticker, timeframe)` and returns the
 * anchored VWAP series. Used by the Dashboard's "Anchored VWAP" badge so the
 * header stat matches whatever timeframe the user has selected on the Charts
 * tab (1d / 5d / 1mo / 3mo / 6mo / 1y).
 *
 * Returns:
 *   - latest:    final anchored VWAP price for the period
 *   - openPrice: first close in the period (anchor point reference)
 *   - hasData:   true iff successful
 */
export default function useAnchoredVwap(ticker, timeframe = "1d") {
  const [state, setState] = useState({
    latest: null,
    openPrice: null,
    hasData: false,
    timeframe,
  });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/stock/price-trend/${ticker}?timeframe=${timeframe}`,
          { timeout: 30000 }
        );
        if (cancelled) return;
        const candles = res.data?.chart_data || [];
        if (!candles.length) {
          setState({ latest: null, openPrice: null, hasData: false, timeframe });
          return;
        }
        const enriched = enrichWithAnchoredVwap(candles);
        const latest = latestAnchoredVwap(enriched);
        const openPrice = candles[0]?.price ?? candles[0]?.close ?? null;
        setState({
          latest,
          openPrice: openPrice != null ? Number(openPrice) : null,
          hasData: latest != null,
          timeframe,
        });
      } catch {
        if (!cancelled) {
          setState({ latest: null, openPrice: null, hasData: false, timeframe });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker, timeframe]);

  return state;
}
